import React, {useEffect, useState} from 'react';
import {
    Alert,
    Badge,
    Box,
    Button,
    CircularProgress,
    Container,
    Divider,
    Grid,
    IconButton,
    Paper,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {Link, useNavigate} from 'react-router-dom';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {getUser} from '../helpers/crypto';
import {clearCart, getCart, getCartTotal, removeFromCart, updateQuantity} from '../helpers/cart';
import {CartItem} from '../types/cartItem';
import {Constants} from "../components/Constants";
import {iconNameToIcon} from "../components/iconNameToIcon";

const CartPage: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [cart, setCart] = useState<CartItem[]>([]);
    const [user, setUser] = useState<any>(null);
    const [ordering, setOrdering] = useState(false);
    const [orderSuccess, setOrderSuccess] = useState(false);
    const [orderId, setOrderId] = useState<number | null>(null);
    const [orderError, setOrderError] = useState('');

    useEffect(() => {
        const u = getUser();
        if (!u) {
            navigate('/login');
            return;
        }
        setUser(u);
        setCart(getCart());
    }, [navigate]);

    const handleUpdateQuantity = (itemId: number, qty: number) => {
        updateQuantity(itemId, qty);
        setCart(getCart());
    };

    const handleRemove = (itemId: number) => {
        removeFromCart(itemId);
        setCart(getCart());
    };

    const handleClear = () => {
        clearCart();
        setCart([]);
    };

    const total = getCartTotal();

    const handleOrder = async () => {
        setOrdering(true);
        setOrderError('');
        try {
            const res = await fetch(`${Constants.SERVER_URL}${Constants.CREATE_ORDER_PREFIX}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user, items: cart, total }),
            });
            const data = await res.json();
            if (data.success) {
                clearCart();
                setCart([]);
                setOrderId(data.order_id ?? null);
                setOrderSuccess(true);
            } else {
                setOrderError(data.error || 'Помилка відправки замовлення');
            }
        } catch {
            setOrderError('Не вдалося з\'єднатися з сервером');
        } finally {
            setOrdering(false);
        }
    };

    const getItemImage = (imgPath1: string | null): string | undefined => imgPath1 ?? undefined;

    const getItemPrice = (entry: CartItem): number =>
        entry.item.priceAkcia > 0 ? entry.item.priceAkcia : entry.item.price;

    return (
        <Box sx={{ bgcolor: '#f7f9fc', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', borderBottom: '1px solid #e0e6ed', position: 'sticky', top: 0, zIndex: 100 }}>
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', alignItems: 'center', height: 70, gap: 2 }}>
                        <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none' }}>
                            <LocalHospitalIcon sx={{ color: '#0077CC', fontSize: 28 }} />
                            <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#1a1a2e' }}>{Constants.APP_LABEL}</Typography>
                        </Box>
                        <Box sx={{ flexGrow: 1 }} />
                        {user && (
                            <Badge
                                badgeContent={user.status !== 'REGULAR' ? React.cloneElement(iconNameToIcon[user.status]) : null}
                                sx={{ '& .MuiBadge-badge': { minWidth: 20, height: 20, p: '2px' } }}
                            >
                                <Button sx={{ textTransform: 'none', color: '#555', fontWeight: 500 }}
                                        onClick={() => navigate('/profile')}>
                                    {user.name} {user.last_name}
                                </Button>
                            </Badge>
                        )}
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/catalog')}
                    sx={{ textTransform: 'none', color: '#555', mb: 3 }}>
                    Повернутися до каталогу
                </Button>

                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                    Кошик
                </Typography>

                {orderSuccess ? (
                    <Paper elevation={0} sx={{ border: '1px solid #c3e6cb', borderRadius: 3, p: 6, textAlign: 'center', bgcolor: '#f0fff4' }}>
                        <Typography sx={{ fontSize: 48, mb: 1 }}>✅</Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: 20, mb: 1, color: '#2e7d32' }}>Замовлення оформлено!</Typography>
                        {orderId && (
                            <Typography sx={{ fontWeight: 600, color: '#0077CC', mb: 0.5 }}>
                                № {orderId}
                            </Typography>
                        )}
                        <Typography sx={{ color: '#555', mb: 3 }}>Підтвердження надіслано на {user?.email}</Typography>
                        <Button variant="contained" sx={{ textTransform: 'none', borderRadius: 2, px: 4 }} onClick={() => navigate('/catalog')}>
                            Продовжити покупки
                        </Button>
                    </Paper>
                ) : cart.length === 0 ? (
                    <Paper elevation={0} sx={{ border: '1px solid #e0e6ed', borderRadius: 3, p: 6, textAlign: 'center' }}>
                        <ShoppingCartOutlinedIcon sx={{ fontSize: 72, color: '#ccc', mb: 2 }} />
                        <Typography sx={{ fontWeight: 600, fontSize: 18, mb: 1 }}>Кошик порожній</Typography>
                        <Typography sx={{ color: '#555', mb: 3 }}>Додайте товари з каталогу</Typography>
                        <Button variant="contained" sx={{ textTransform: 'none', borderRadius: 2, px: 4 }} onClick={() => navigate('/catalog')}>
                            Перейти до каталогу
                        </Button>
                    </Paper>
                ) : (
                    <Grid container spacing={3}>
                        {/* Cart items */}
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Paper elevation={0} sx={{ border: '1px solid #e0e6ed', borderRadius: 3, overflow: 'hidden' }}>
                                {cart.map((entry, idx) => (
                                    <Box key={entry.item.id}>
                                        <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                                            {/* Top row: image + info */}
                                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                                <Box
                                                    component="img"
                                                    src={getItemImage(entry.item.imgPath1)}
                                                    alt={entry.item.name}
                                                    onError={(e: any) => { e.target.style.display = 'none'; }}
                                                    sx={{ width: { xs: 60, sm: 80 }, height: { xs: 60, sm: 80 }, objectFit: 'contain', borderRadius: 2, bgcolor: '#f7f9fc', flexShrink: 0, p: 0.5 }}
                                                />
                                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                    <Typography sx={{ fontSize: 11, color: '#999' }}>{entry.item.shortName}</Typography>
                                                    <Typography sx={{ fontWeight: 600, fontSize: { xs: 13, sm: 14 }, lineHeight: 1.4, mb: 0.5,
                                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {entry.item.name}
                                                    </Typography>
                                                    <Typography sx={{ fontWeight: 700, color: entry.item.priceAkcia > 0 ? '#e53935' : '#0077CC', fontSize: 14 }}>
                                                        {getItemPrice(entry).toFixed(2)} грн
                                                    </Typography>
                                                </Box>
                                                {!isMobile && (
                                                    <>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                                            <IconButton size="small" onClick={() => handleUpdateQuantity(entry.item.id, entry.quantity - 1)}
                                                                sx={{ border: '1px solid #e0e6ed', borderRadius: 1.5 }}>
                                                                <RemoveIcon fontSize="small" />
                                                            </IconButton>
                                                            <Typography sx={{ minWidth: 32, textAlign: 'center', fontWeight: 600 }}>
                                                                {entry.quantity}
                                                            </Typography>
                                                            <IconButton size="small" onClick={() => handleUpdateQuantity(entry.item.id, entry.quantity + 1)}
                                                                sx={{ border: '1px solid #e0e6ed', borderRadius: 1.5 }}>
                                                                <AddIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                        <Typography sx={{ fontWeight: 700, fontSize: 15, minWidth: 90, textAlign: 'right', flexShrink: 0 }}>
                                                            {(getItemPrice(entry) * entry.quantity).toFixed(2)} грн
                                                        </Typography>
                                                        <IconButton size="small" onClick={() => handleRemove(entry.item.id)}
                                                            sx={{ color: '#bbb', '&:hover': { color: '#e53935' }, flexShrink: 0 }}>
                                                            <DeleteOutlinedIcon />
                                                        </IconButton>
                                                    </>
                                                )}
                                            </Box>
                                            {/* Mobile bottom row: qty + subtotal + delete */}
                                            {isMobile && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <IconButton size="small" onClick={() => handleUpdateQuantity(entry.item.id, entry.quantity - 1)}
                                                            sx={{ border: '1px solid #e0e6ed', borderRadius: 1.5 }}>
                                                            <RemoveIcon fontSize="small" />
                                                        </IconButton>
                                                        <Typography sx={{ minWidth: 28, textAlign: 'center', fontWeight: 600, fontSize: 14 }}>
                                                            {entry.quantity}
                                                        </Typography>
                                                        <IconButton size="small" onClick={() => handleUpdateQuantity(entry.item.id, entry.quantity + 1)}
                                                            sx={{ border: '1px solid #e0e6ed', borderRadius: 1.5 }}>
                                                            <AddIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                    <Typography sx={{ fontWeight: 700, fontSize: 14, minWidth: 80, textAlign: 'right', flexGrow: 1 }}>
                                                        {(getItemPrice(entry) * entry.quantity).toFixed(2)} грн
                                                    </Typography>
                                                    <IconButton size="small" onClick={() => handleRemove(entry.item.id)}
                                                        sx={{ color: '#bbb', '&:hover': { color: '#e53935' } }}>
                                                        <DeleteOutlinedIcon />
                                                    </IconButton>
                                                </Box>
                                            )}
                                        </Box>
                                        {idx < cart.length - 1 && <Divider />}
                                    </Box>
                                ))}

                                <Divider />
                                <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button size="small" startIcon={<DeleteOutlinedIcon />} onClick={handleClear}
                                        sx={{ textTransform: 'none', color: '#999', '&:hover': { color: '#e53935' } }}>
                                        Очистити кошик
                                    </Button>
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Summary */}
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Paper elevation={0} sx={{ border: '1px solid #e0e6ed', borderRadius: 3, p: 3, position: 'sticky', top: 90 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: 17, mb: 2.5 }}>Підсумок</Typography>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography sx={{ color: '#555' }}>
                                        Товарів: {cart.reduce((s, c) => s + c.quantity, 0)} шт.
                                    </Typography>
                                </Box>

                                {cart.map(entry => (
                                    <Box key={entry.item.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography sx={{ fontSize: 13, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '65%' }}>
                                            {entry.item.shortName || entry.item.name}
                                        </Typography>
                                        <Typography sx={{ fontSize: 13, color: '#555', flexShrink: 0 }}>
                                            × {entry.quantity}
                                        </Typography>
                                    </Box>
                                ))}

                                <Divider sx={{ my: 2 }} />

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Разом:</Typography>
                                    <Typography sx={{ fontWeight: 700, fontSize: 22, color: '#0077CC' }}>
                                        {total.toFixed(2)} грн
                                    </Typography>
                                </Box>

                                {orderError && <Alert severity="error" sx={{ mb: 1.5 }}>{orderError}</Alert>}
                                <Button variant="contained" fullWidth size="large"
                                    disabled={ordering}
                                    onClick={handleOrder}
                                    sx={{ textTransform: 'none', borderRadius: 2, py: 1.5, mb: 1.5 }}>
                                    {ordering ? <CircularProgress size={22} color="inherit" /> : 'Оформити замовлення'}
                                </Button>
                                <Button variant="outlined" fullWidth
                                    sx={{ textTransform: 'none', borderRadius: 2 }}
                                    onClick={() => navigate('/catalog')}>
                                    Продовжити покупки
                                </Button>

                                <Alert severity="info" sx={{ mt: 2.5, fontSize: 12 }}>
                                    Доставка розраховується при оформленні замовлення
                                </Alert>
                            </Paper>
                        </Grid>
                    </Grid>
                )}
            </Container>
        </Box>
    );
};

export default CartPage;
